/**
 * AuthService
 *
 * Core authentication and session orchestration.
 * Owns: supabase.auth.* operations (signInWithPassword, signUp, signOut, getSession, setSession, refreshSession)
 *
 * Handles: login, logout, session restore, MFA verification, signup/register flows.
 * Does NOT handle: password resets (PasswordService), email changes (EmailChangeService), profile updates (ProfileService)
 */

import { supabase } from '@/lib/supabase';
import type { Session } from '@supabase/supabase-js';
import { userService } from '@/services/UserService';
import { mfaService } from '@/services/MFAService';
import { mfaFactorsRepository } from '@/repositories/mfaFactorsRepository';
import type { Factor } from '@/types/mfa';
import { AuthErrorCode } from '@/types/auth';
import type {
  LoginRequest,
  RegisterRequest,
  AuthSuccessResponse,
  AuthErrorResponse,
  User
} from '@/types/auth';

/**
 * Result of session initialization
 */
export type InitializeAuthResult =
  | { status: 'unauthenticated' }
  | {
      status: 'authenticated';
      user: User;
      session: Session;
    };

/**
 * Result of login operation
 */
export type LoginResult =
  | {
      type: 'success';
      sessionUser: User;
      session: Session;
    }
  | {
      type: 'mfa_required';
      challenge: {
        challengeId: string;
        factorId: string;
        expiresAt: number;
        availableFactors: Factor[];
        pendingSession: {
          user: User;
          session: Session;
        };
      };
    }
  | {
      type: 'error';
      error: AuthErrorCode;
      message: string;
    };

/**
 * Pending session for MFA verification
 */
export interface MFAPendingSession {
  user: User;
  session: {
    access_token: string;
    expires_at?: number;
  };
}

/**
 * Result of MFA verification
 */
export interface MFAVerifyResult {
  success: true;
  user: User;
  session: Session;
}

/**
 * AuthService
 * Core authentication operations
 */
class AuthService {
  /**
   * Build User object from Supabase session metadata
   *
   * Used for session restoration to avoid hanging on database queries during initial page load.
   *
   * @param session - Supabase session object
   * @returns User object built from session metadata
   */
  buildUserFromSession(session: Session): User {
    return {
      id: session.user.id,
      email: session.user.email ?? '',
      displayName:
        session.user.user_metadata?.['display_name'] ??
        session.user.user_metadata?.['full_name'] ??
        session.user.email?.split('@')[0] ??
        'User',
      createdAt: session.user.created_at ?? new Date().toISOString(),
      emailVerified: Boolean(session.user.email_confirmed_at),
      lastLoginAt: session.user.last_sign_in_at ?? new Date().toISOString(),
      licenseKey: '',
      role: session.user.app_metadata?.['role'] || 'user', // ONLY from app_metadata, never user_metadata
      user_metadata: session.user.user_metadata
    };
  }

  /**
   * Initialize authentication state by checking for existing session
   *
   * @returns Clean state snapshot for Provider to apply atomically
   */
  async initializeAuth(): Promise<InitializeAuthResult> {
    try {
      // Get current session from Supabase
      const {
        data: { session },
        error
      } = await supabase.auth.getSession();

      if (error || !session) {
        console.log('ℹ️ AuthService: No existing session found');
        return { status: 'unauthenticated' };
      }

      // Get user data from repository via service layer
      // Use retry logic to handle race condition with DB trigger creating public.users
      let domainUser = await userService.get(session.user.id);

      if (!domainUser) {
        // Retry up to 3 times with increasing delays (500ms, 1000ms, 1500ms)
        // This handles race condition when trigger hasn't created user record yet
        console.log('⏳ AuthService: User not found, retrying with delay...');

        for (let attempt = 1; attempt <= 3; attempt++) {
          await new Promise(resolve => setTimeout(resolve, attempt * 500));
          console.log(`🔄 AuthService: Retry attempt ${attempt}/3`);

          domainUser = await userService.get(session.user.id);
          if (domainUser) {
            console.log('✅ AuthService: User found on retry attempt', attempt);
            break;
          }
        }
      }

      if (!domainUser) {
        console.log('❌ AuthService: User data not found after retries, clearing session');
        await supabase.auth.signOut();
        return { status: 'unauthenticated' };
      }

      // Check if account is active or pending payment (new users completing registration)
      if (domainUser.status !== 'active' && domainUser.status !== 'pending_payment') {
        console.log('❌ AuthService: User account is not active, clearing session');
        await supabase.auth.signOut();
        return { status: 'unauthenticated' };
      }

      // Register device (same as login flow - ensures device registration on session restore)
      try {
        const { deviceService } = await import('@/services/DeviceService');
        const { getOrCreateDeviceId, generateDeviceName } = await import('@/utils/deviceDetection');

        const deviceId = getOrCreateDeviceId();
        const deviceName = generateDeviceName();

        await deviceService.registerDevice(session.user.id, deviceId, deviceName, 'local');
        console.log(`📱 AuthService: Device registered - ${deviceName} (${deviceId.slice(0, 5)})`);
      } catch (deviceError) {
        console.error('❌ AuthService: Device registration failed:', deviceError);
        // Don't fail session restore if device registration fails
      }

      // Build User object for auth context
      // Domain user data is already in camelCase from repository
      const user: User = {
        id: session.user.id,
        email: session.user.email ?? '', // Supabase Auth guarantees email for authenticated users
        displayName: domainUser.displayName ?? domainUser.fullName ?? '',
        createdAt: domainUser.createdAt ?? '',
        emailVerified: !!session.user.email_confirmed_at,
        lastLoginAt: session.user.last_sign_in_at || new Date().toISOString(),
        licenseKey: '', // TODO: Implement licensing in future WU
        role: session.user.app_metadata?.['role'] || 'user' // Admin users have role='admin'
      };

      console.log('✅ AuthService: Session restored for', user.email);

      return {
        status: 'authenticated',
        user,
        session
      };
    } catch (error) {
      console.error('❌ AuthService: Session initialization error:', error);
      return { status: 'unauthenticated' };
    }
  }

  /**
   * Authenticate user with email and password
   *
   * Handles MFA detection and device registration.
   *
   * @param request - Login credentials
   * @returns Login result (success, MFA required, or error)
   */
  async login(request: LoginRequest): Promise<LoginResult> {
    console.log('🔐 AuthService: Login attempt for', request.email);

    try {
      // 1. Authenticate with Supabase Auth
      const { data, error } = await supabase.auth.signInWithPassword({
        email: request.email,
        password: request.password
      });

      if (error || !data.user || !data.session) {
        console.log('❌ AuthService: Login failed:', error?.message);

        return {
          type: 'error',
          error: 'INVALID_CREDENTIALS' as AuthErrorCode,
          message: error?.message || 'Invalid email or password'
        };
      }

      // 2. Get user data from repository via service layer
      // Use retry logic to handle race condition with DB trigger creating public.users
      let domainUser = await userService.get(data.user.id);

      if (!domainUser) {
        // Retry up to 3 times with increasing delays (500ms, 1000ms, 1500ms)
        // This handles race condition when trigger hasn't created user record yet
        console.log('⏳ AuthService: User not found, retrying with delay...');

        for (let attempt = 1; attempt <= 3; attempt++) {
          await new Promise(resolve => setTimeout(resolve, attempt * 500));
          console.log(`🔄 AuthService: Retry attempt ${attempt}/3`);

          domainUser = await userService.get(data.user.id);
          if (domainUser) {
            console.log('✅ AuthService: User found on retry attempt', attempt);
            break;
          }
        }
      }

      if (!domainUser) {
        console.error('❌ AuthService: User data not found in database after retries');

        return {
          type: 'error',
          error: 'USER_NOT_FOUND' as AuthErrorCode,
          message: 'Account data not found'
        };
      }

      // 3. Check if account is active or pending payment (new users completing registration)
      if (domainUser.status !== 'active' && domainUser.status !== 'pending_payment') {
        return {
          type: 'error',
          error: 'ACCOUNT_NOT_ACTIVATED' as AuthErrorCode,
          message: 'Account is not active'
        };
      }

      // 4. Register device (preserve existing functionality)
      try {
        const { deviceService } = await import('@/services/DeviceService');
        const { getOrCreateDeviceId, generateDeviceName } = await import('@/utils/deviceDetection');

        const deviceId = getOrCreateDeviceId();
        const deviceName = generateDeviceName();

        await deviceService.registerDevice(data.user.id, deviceId, deviceName, 'local');
        console.log(`📱 AuthService: Device registered - ${deviceName} (${deviceId.slice(0, 5)})`);
      } catch (deviceError) {
        console.error('❌ AuthService: Device registration failed:', deviceError);
        // Don't fail login if device registration fails
      }

      // 5. Build User object for context
      // Domain user data is already in camelCase from repository
      const user: User = {
        id: data.user.id,
        email: data.user.email ?? '', // Supabase Auth guarantees email for authenticated users
        displayName: domainUser.displayName ?? domainUser.fullName ?? '',
        createdAt: domainUser.createdAt ?? '',
        emailVerified: !!data.user.email_confirmed_at,
        lastLoginAt: data.user.last_sign_in_at || new Date().toISOString(),
        licenseKey: '', // TODO: Implement licensing in future WU
        role: data.user.app_metadata?.['role'] || 'user', // Admin users have role='admin'
        user_metadata: data.user.user_metadata // Include metadata for reauth checks
      };

      // 6. Check for MFA enrollment
      const factorsResult = await mfaService.listFactors();

      if (factorsResult.success && factorsResult.data.length > 0) {
        // User has MFA enrolled - create challenge
        console.log('🔐 AuthService: MFA enrolled, creating challenge');

        // Select factor: prioritize most recently used, fallback to first
        const factors = factorsResult.data;
        const mostRecentFactor = factors.slice().sort((a, b) => {
          if (!a.last_used_at) return 1;
          if (!b.last_used_at) return -1;
          return new Date(b.last_used_at).getTime() - new Date(a.last_used_at).getTime();
        })[0];

        const selectedFactor = mostRecentFactor || factors[0];

        if (!selectedFactor) {
          console.error('❌ AuthService: No factor found despite length check');
          // Continue with normal login
        } else {
          const challengeResult = await mfaService.createChallenge(selectedFactor.id);

          if (!challengeResult.success) {
            // Challenge creation failed - fall back to normal login
            console.error('❌ AuthService: MFA challenge creation failed:', challengeResult.error);
            // Continue with normal login (don't block user)
          } else {
            console.log('🔐 AuthService: MFA challenge created, awaiting verification');

            // Return MFA required result
            return {
              type: 'mfa_required',
              challenge: {
                challengeId: challengeResult.data.challenge_id,
                factorId: selectedFactor.id,
                expiresAt: challengeResult.data.expires_at,
                availableFactors: factors,
                pendingSession: { user, session: data.session }
              }
            };
          }
        }
      }

      // 7. No MFA or MFA failed - complete login normally
      console.log('✅ AuthService: Login successful for', request.email);

      return {
        type: 'success',
        sessionUser: user,
        session: data.session
      };
    } catch (error) {
      console.error('❌ AuthService: Login error:', error);

      return {
        type: 'error',
        error: 'NETWORK_ERROR' as AuthErrorCode,
        message: 'An unexpected error occurred during login'
      };
    }
  }

  /**
   * Verify MFA code and complete login
   *
   * @param code - TOTP code from user
   * @param usedFactorId - Factor ID that was used
   * @param challengeId - Challenge ID from MFA challenge
   * @param pendingSession - Pending session from login
   * @returns Verified session data
   */
  async verifyMFALogin(
    code: string,
    usedFactorId: string,
    challengeId: string,
    pendingSession: MFAPendingSession
  ): Promise<MFAVerifyResult> {
    console.log('🔐 AuthService: Verifying MFA code for login');

    // Verify the TOTP code
    const result = await mfaService.verifyLogin(challengeId, code, usedFactorId);

    if (!result.success) {
      // Verification failed
      throw new Error(result.error.message);
    }

    // MFA verified! The verify call returns the upgraded AAL2 session
    // Use setSession() to explicitly persist it to localStorage
    console.log('🔐 AuthService: MFA verified, persisting upgraded session...');

    const verifyData = result.data as {
      session?: { access_token: string; refresh_token: string; expires_at?: number };
    };

    if (verifyData?.session) {
      // Prefer direct persistence if SDK returns a session
      console.log('✅ AuthService: Got session from mfa.verify(), persisting with setSession()');
      const { error: setSessionError } = await supabase.auth.setSession(verifyData.session);

      if (setSessionError) {
        console.error('❌ AuthService: Failed to persist session:', setSessionError);
        throw new Error('Failed to persist upgraded session');
      }
    } else {
      // Fallback: call refresh with explicit refresh_token
      console.log('⚠️ AuthService: No session from verify, falling back to refreshSession()');
      const {
        data: { session: currentSession }
      } = await supabase.auth.getSession();

      if (currentSession?.refresh_token) {
        await supabase.auth.refreshSession({ refresh_token: currentSession.refresh_token });
      } else {
        throw new Error('No refresh token available');
      }
    }

    // Get the persisted session
    const {
      data: { session: upgradedSession }
    } = await supabase.auth.getSession();

    if (!upgradedSession) {
      console.error('❌ AuthService: No session after persistence');
      throw new Error('Failed to get upgraded session');
    }

    console.log('✅ AuthService: Upgraded AAL2 session persisted to storage');

    // Store AAL2 hint in sessionStorage for restore logic
    const expiresAtMs = (upgradedSession.expires_at || 0) * 1000;
    sessionStorage.setItem('aal2_until', String(expiresAtMs));
    console.log('✅ AuthService: Stored AAL2 hint until', new Date(expiresAtMs).toISOString());

    // Update last_used_at for the factor used
    try {
      await mfaFactorsRepository.updateLastUsedAt(
        pendingSession.user.id,
        usedFactorId,
        new Date().toISOString()
      );
      console.log(`✅ AuthService: Updated last_used_at for factor ${usedFactorId}`);
    } catch (err) {
      console.warn('⚠️ AuthService: Failed to update last_used_at (non-blocking):', err);
    }

    console.log('✅ AuthService: MFA verification successful, login complete with AAL2 session');

    return {
      success: true,
      user: pendingSession.user,
      session: upgradedSession
    };
  }

  /**
   * Low-level signup primitive - creates Supabase auth.users record
   *
   * Used by AccountActivation (invited users) and future admin user creation flows.
   * This is a thin wrapper around supabase.auth.signUp() with error normalization.
   *
   * Does NOT perform business logic like:
   * - License key validation
   * - Password confirmation checks
   * - Onboarding state initialization
   *
   * For full self-serve registration with business rules, use register() instead.
   *
   * @param credentials - Email, password, and optional metadata
   * @returns Success with userId or error
   */
  async signup(credentials: {
    email: string;
    password: string;
    options?: {
      emailRedirectTo?: string;
      data?: Record<string, unknown>;
      captchaToken?: string;
    };
  }): Promise<{ success: true; userId: string } | { success: false; error: AuthErrorCode; message: string }> {
    console.log('📝 AuthService: Signup attempt for', credentials.email);

    try {
      // Call Supabase Auth signUp with full options passthrough
      // Only include options if provided (to satisfy exactOptionalPropertyTypes)
      const signUpParams: {
        email: string;
        password: string;
        options?: { emailRedirectTo?: string; data?: object; captchaToken?: string };
      } = {
        email: credentials.email,
        password: credentials.password
      };
      if (credentials.options) {
        signUpParams.options = credentials.options;
      }
      const { data, error } = await supabase.auth.signUp(signUpParams);

      if (error) {
        console.error('❌ AuthService: Signup error:', error);

        // Normalize Supabase errors to AuthErrorCode
        if (error.message.includes('already registered') || error.message.includes('already exists')) {
          return {
            success: false,
            error: AuthErrorCode.EMAIL_ALREADY_EXISTS,
            message: 'An account with this email already exists. Please try logging in.'
          };
        }

        if (error.message.includes('invalid email')) {
          return {
            success: false,
            error: AuthErrorCode.INVALID_EMAIL_FORMAT,
            message: 'Please provide a valid email address.'
          };
        }

        if (error.message.includes('weak password') || error.message.includes('password')) {
          return {
            success: false,
            error: AuthErrorCode.PASSWORD_TOO_WEAK,
            message: 'Password does not meet security requirements.'
          };
        }

        // Generic error for other cases
        return {
          success: false,
          error: AuthErrorCode.SERVER_ERROR,
          message: error.message || 'Signup failed. Please try again.'
        };
      }

      if (!data.user) {
        return {
          success: false,
          error: AuthErrorCode.SERVER_ERROR,
          message: 'Signup succeeded but no user data returned.'
        };
      }

      console.log('✅ AuthService: Signup successful for user:', data.user.id);

      // Supabase auto-logs in after signUp
      // The SIGNED_IN event will be handled by onAuthStateChange listener in Provider

      return {
        success: true,
        userId: data.user.id
      };
    } catch (error) {
      console.error('❌ AuthService: Unexpected signup error:', error);

      return {
        success: false,
        error: AuthErrorCode.SERVER_ERROR,
        message: 'An unexpected error occurred. Please try again.'
      };
    }
  }

  /**
   * High-level registration with business logic
   *
   * For future self-serve registration flow with:
   * - License key validation
   * - Password confirmation checks
   * - Onboarding state initialization
   * - Display name setup
   *
   * Currently not implemented - registration is invitation-only via AccountActivation.
   * When implemented, this should call signup() internally after performing business validations.
   *
   * @param request - Registration request
   * @returns Success or error
   */
  async register(request: RegisterRequest): Promise<AuthSuccessResponse | AuthErrorResponse> {
    console.log('📝 AuthService: Registration attempt for', request.email);

    // TODO: Implement full registration flow in future sprint
    // 1. Validate license key exists and is not already used
    // 2. Validate password === confirmPassword
    // 3. Call signup() to create auth.users record
    // 4. Initialize onboarding state in public.users
    // 5. Potentially create Stripe linkage
    // 6. Return success with user data

    return {
      success: false,
      error: AuthErrorCode.SERVER_ERROR,
      message: 'Self-serve registration is not yet implemented. Please use an invitation link.'
    };
  }

  /**
   * Set session from access and refresh tokens
   *
   * Used after account activation when Edge Function returns session tokens.
   * Components should call this method instead of importing supabase directly.
   *
   * @param accessToken - JWT access token
   * @param refreshToken - Refresh token for session renewal
   * @returns Success status with optional error message
   */
  async setSessionFromTokens(
    accessToken: string,
    refreshToken: string
  ): Promise<{ success: boolean; error?: string }> {
    console.log('🔐 AuthService: Setting session from tokens');

    try {
      const { error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (error) {
        console.error('❌ AuthService: Failed to set session:', error);
        return { success: false, error: error.message };
      }

      console.log('✅ AuthService: Session set successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ AuthService: Unexpected error setting session:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Log out current user
   */
  async logout(): Promise<void> {
    console.log('🚪 AuthService: Logout initiated');

    try {
      // Sign out from Supabase (clears session)
      await supabase.auth.signOut();
      console.log('✅ AuthService: Logout completed');
    } catch (error) {
      console.error('❌ AuthService: Logout error:', error);
      // Continue with local logout even if service call fails
      throw error;
    }
  }

  /**
   * Revoke all sessions for the current user across all devices
   *
   * Calls the revoke-sessions-and-audit Edge Function for server-side revocation
   * and audit logging, then signs out globally via Supabase Auth.
   *
   * The Edge Function call is non-blocking — a failure is logged but does not
   * prevent the global sign-out from completing.
   *
   * @param userId - The authenticated user's ID (must match JWT — Edge Function verifies this)
   * @param userAgent - navigator.userAgent for audit logging (IP derived server-side)
   */
  async logoutEverywhere(userId: string, userAgent: string): Promise<void> {
    console.log('🚪 AuthService: Logout everywhere initiated');

    // Call Edge Function for server-side session revocation + audit log (non-blocking)
    try {
      const { error: edgeFunctionError } = await supabase.functions.invoke('revoke-sessions-and-audit', {
        body: {
          user_id: userId,
          change_method: 'manual_logout_everywhere',
          user_agent: userAgent
        }
        // Note: Authorization header automatically added by supabase.functions.invoke()
        // Edge Function verifies user.id matches body.user_id (prevents abuse)
      });

      if (edgeFunctionError) {
        console.error('❌ AuthService: revoke-sessions-and-audit Edge Function failed:', edgeFunctionError);
      } else {
        console.log('✅ AuthService: revoke-sessions-and-audit Edge Function called successfully');
      }
    } catch (edgeFunctionCallError) {
      console.error('❌ AuthService: Failed to call revoke-sessions-and-audit Edge Function:', edgeFunctionCallError);
      // Non-blocking: global sign-out proceeds regardless
    }

    // Sign out globally — ends all sessions including this one
    try {
      await supabase.auth.signOut({ scope: 'global' });
      console.log('✅ AuthService: Global sign-out completed');
    } catch (error) {
      console.error('❌ AuthService: Global sign-out error:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
